// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  getOrganisations,
  updateCurrentOrganisation
} from '../actions/organisations/organisationCreators';
import { updateCurrentLocation } from '../actions/locations/locationCreators';
import { logout } from '../actions/authentication/authCreators';
import { updateUserDefaultPage } from '../actions/users/settingsCreators';
import onClickOutside from 'react-onclickoutside';
import { Link } from 'react-router';
import {
  Collapse,
  Button,
  Glyphicon,
  Grid,
  Col,
  Row,
  Checkbox
} from 'react-bootstrap';
import GlobalMenu from '../components/menuItems/GlobalMenu';
import CustomerMenu from '../components/menuItems/CustomerMenu';
import LocationMenu from '../components/menuItems/LocationMenu';
import { CSSTransitionGroup } from 'react-transition-group';
import type { Dispatch } from 'redux';
import type { Organisation, Authentication } from '../types';

import { getSetting } from '../actions/settings/settingsCreators';

type Props = {
  locations: Object[],
  organisations: Organisation[],
  currentOrganisation: Organisation,
  currentLocation: Object,
  authentication: Authentication,
  settings: any,
  logout: () => void,
  updateUserDefaultPage: (p: string) => void,
  updateCurrentOrganisation: (oid: number, s?: boolean) => Promise<void>,
  getOrganisations: Function,
  updateCurrentLocation: Function,
  getSetting: (s: string) => void,
  router: any
};

type State = {
  open: boolean,
  showLocations: boolean,
  showCustomers: boolean,
  menuWidth: number
};

class NineDotMenu extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      open: false,
      showLocations: false,
      showCustomers: false,
      menuWidth: 6
    };
  }

  parentOrganisations = (organisations: Organisation[]): Organisation[] => {
    return organisations.filter(o => !o.parentId);
  };

  componentDidMount() {
    const showLocations =
      this.props.locations && this.props.locations.length > 0;
    const showCustomers =
      this.parentOrganisations(this.props.organisations).length > 0;
    this.setState({ showCustomers });
    this.setMenuWidth(showLocations);

    const { getSetting } = this.props;

    getSetting('supportEmailAddress');
  }

  componentWillReceiveProps(nextProps: Props) {
    const oldLocations = JSON.stringify(this.props.locations);
    const newLocations = JSON.stringify(nextProps.locations);
    if (oldLocations !== newLocations) {
      const showLocations =
        nextProps.locations && nextProps.locations.length > 0;
      this.setMenuWidth(showLocations);
    }
    if (this.props.organisations !== nextProps.organisations) {
      const showCustomers =
        this.parentOrganisations(nextProps.organisations).length > 0;
      this.setState({ showCustomers });
    }
  }

  setMenuWidth = (showLocations: boolean) => {
    this.setState({ showLocations });
    let menuWidth;
    if (showLocations) {
      menuWidth = 4;
      // immediately create space for menu to fade in
      this.setState({ menuWidth });
    } else {
      // delay until menu has faded out
      menuWidth = 6;
      setTimeout(() => this.setState({ menuWidth }), 750);
    }
  };

  logout = (event: Event) => {
    event.preventDefault();
    this.props.logout();
  };

  closeMenu = () => {
    this.setState({ open: false });
  };

  handleClickOutside = (event: Event) => {
    this.closeMenu();
  };

  handleKeyPress = (e: KeyboardEvent) => {
    const keyPressed = e.keyCode;
    if (keyPressed === 27) {
      this.closeMenu();
    }
  };

  setDefaultPage = (event: SyntheticInputEvent<*>) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      const page = window.location.pathname;
      this.props.updateUserDefaultPage(page);
    } else {
      this.props.updateUserDefaultPage('/');
    }
  };

  render() {
    const {
      organisations,
      currentOrganisation,
      updateCurrentOrganisation,
      getOrganisations,
      locations,
      currentLocation,
      updateCurrentLocation,
      authentication: { user: { defaultPage, isInternalAdminUser } },
      settings
    } = this.props;

    const currentPageIsDefault = window.location.pathname === defaultPage;

    const hasGlobal = isInternalAdminUser ? 'global' : '';
    const hasCustomers = organisations.length ? 'customers' : '';
    const hasLocations = locations.length ? 'locations' : '';

    const parents = organisations.filter(o => !o.parentId);

    return (
      <div
        id="nine-dot-menu"
        className="nine-dot-menu"
        onKeyDown={this.handleKeyPress}
        onBlur={this.handleKeyPress}
        role="presentation"
      >
        <Button
          className="trigger"
          onClick={() => this.setState({ open: !this.state.open })}
          aria-label="Main Navigation"
          aria-expanded={this.state.open}
        >
          <Glyphicon glyph="th" />
        </Button>
        <Collapse
          className={`wrapper ${hasGlobal} ${hasCustomers} ${hasLocations}`}
          in={this.state.open}
        >
          <div>
            <Grid className="nine-dot-inner">
              <div className="text-center">
                {isInternalAdminUser && (
                  <div className="ndm-module">
                    <GlobalMenu closeMenu={this.closeMenu} />
                  </div>
                )}

                {this.state.showCustomers && (
                  <div className="ndm-module">
                    <CustomerMenu
                      organisations={parents}
                      currentOrganisation={currentOrganisation}
                      updateCurrentOrganisation={updateCurrentOrganisation}
                      getOrganisations={getOrganisations}
                      closeMenu={this.closeMenu}
                      permissions={currentOrganisation.permissions}
                      router={this.props.router}
                    />
                  </div>
                )}

                <CSSTransitionGroup
                  transitionName="fade"
                  transitionEnterTimeout={500}
                  transitionLeaveTimeout={300}
                >
                  {this.state.showLocations && (
                    <div className="ndm-module">
                      <LocationMenu
                        locations={locations}
                        currentLocation={currentLocation}
                        currentOrganisation={currentOrganisation}
                        updateCurrentLocation={updateCurrentLocation}
                        closeMenu={this.closeMenu}
                        permissions={currentLocation.permissions}
                      />
                    </div>
                  )}
                </CSSTransitionGroup>
              </div>

              <Row className="actions">
                <Col xs={12}>
                  <ul className="list-inline">
                    <li>
                      <Link to="/account" onClick={this.closeMenu}>
                        My Account
                      </Link>
                    </li>
                    {settings &&
                      settings.supportEmailAddress && (
                        <li>
                          <a
                            href={`mailto:${
                              settings.supportEmailAddress.value
                            }`}
                          >
                            Contact Us
                          </a>
                        </li>
                      )}
                    <li className="default-page">
                      {!(
                        window.location.pathname === '/' && defaultPage === '/'
                      ) && (
                        <Checkbox
                          inline
                          onChange={this.setDefaultPage}
                          checked={currentPageIsDefault}
                        >
                          Start here after login
                        </Checkbox>
                      )}
                    </li>
                    <li className="pull-right">
                      <a href="/login" onClick={e => this.logout(e)}>
                        Logout
                      </a>
                    </li>
                  </ul>
                </Col>
              </Row>
            </Grid>
          </div>
        </Collapse>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    organisations: state.organisations,
    currentOrganisation: state.currentOrganisation,
    locations: state.locations,
    currentLocation: state.currentLocation,
    authentication: state.authentication,
    settings: state.settings
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>) {
  return bindActionCreators(
    {
      getOrganisations,
      updateCurrentOrganisation,
      updateCurrentLocation,
      updateUserDefaultPage,
      logout,
      getSetting
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(
  onClickOutside(NineDotMenu)
);



// WEBPACK FOOTER //
// ./src/containers/NineDotMenu.js